import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Grid,
  Box,
  Card,
  IconButton,
  Input,
  InputAdornment,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import AddLanguageDialog from "popup/AddLanguageDialog";
import AddAlarmTypeDialog from "popup/AddAlarmTypeDialog";
import AddIcon from "@mui/icons-material/Add";
import { useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import { get } from "aws-amplify/api";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { post } from "aws-amplify/api";
import { APIStatusDialog } from "components/APIStatusDialog";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import { GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import AddNotePatterns from "popup/AddNotePatterns";
import ConfirmDeleteDialogNote from "popup/ConfirmDeleteDialogNote";
import EncodingSettingsMenu from "../components/EncodingSettingsMenu";
import PatternSettingsMenu from "../components/PatternSettingsMenu";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";
import EventHistorySettingsMenu from "../components/EventHistorySettingsMenu";

const COLUMN_VISIBILITY_KEY = "columnVisibility_settings";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};
function NotePatterns() {
  const [state, dispatch] = useReducer(reducer, { audit: [], fetched: false });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAlarmTypeIdToDelete, setSelectedAlarmTypeIdToDelete] =
    useState(null);
  const location = useLocation();
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [editedRows, setEditedRows] = useState({});
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [languages, setLanguages] = useState([]);
  const formatLanguageName = (languages) => {
    return languages.charAt(0).toUpperCase() + languages.slice(1).toLowerCase();
  };
  const [tableParentIsMD, setTableParentIsMD] = useState(false);

  const toolbarRef = useRef(null);

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const [openLanguageDialog, setOpenLanguageDialog] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  const [openAlarmTypeDialog, setOpenAlarmTypeDialog] = useState(false);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null); // Aggiungi uno stato per la riga selezionata
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };
  const [refreshLanguages, setRefreshLanguages] = useState(() => () => {});
  

  useEffect(() => {
    const savedColumnVisibility = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (savedColumnVisibility) {
      setColumnVisibilityModel(JSON.parse(savedColumnVisibility));
    } else {
      // Set default visibility to only show specified columns
      setColumnVisibilityModel({
        id: false,
      });
    }
  }, []);

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(newModel));
  };

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower)
        );
    }
    return matchesAllFilters;
  };
  const filteredRows = state.audit.filter(applyStoreFiltering);
  const filteredRowsCount = filteredRows.length;
  const handleOpenLanguageDialog = () => {
    setOpenLanguageDialog(true);
  };
  const handleCloseLanguageDialog = () => {
    setOpenLanguageDialog(false);
  };
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowModes.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleteDialogOpen(false);
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Deleting note pattern"),
      });
      const restOperation = await tlmDeletePI(selectedAlarmTypeIdToDelete);

      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Note pattern successfully deleted"),
        });
        dispatch({
          type: "DATA_RECEIVED",
          value: state.audit.filter(
            (row) => row.pattern_type_id !== selectedAlarmTypeIdToDelete
          ),
        });
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to delete note pattern"),
        });
      }

      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to delete note pattern"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false); // Chiudi il dialogo senza fare nulla
  };

  const processRowUpdate = async (newRow) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Modifying"),
      });

      const descriptionBody = languageColumns.reduce((acc, column) => {
        acc[column.field] = newRow[column.field] || "";
        return acc;
      }, {});

      const restOperation = await tlmModifyDescription(
        newRow.pattern_type_id,
        descriptionBody
      );
      const response = await restOperation.response;

      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Modification successfully saved"),
        });
        const updatedRow = state.audit.map((row) =>
          row.pattern_type_id === newRow.pattern_type_id
            ? { ...row, ...newRow }
            : row
        );
        dispatch({ type: "DATA_RECEIVED", value: updatedRow });
        setTimeout(() => {
          setApiStatus((prevState) => ({ ...prevState, open: false }));
        }, 2000);
        return newRow;
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to save modification"),
        });
        setTimeout(() => {
          setApiStatus((prevState) => ({ ...prevState, open: false }));
        }, 2000);
        return newRow;
      }
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Note Pattern Failed to save modification"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
      return newRow;
    }
  };

  const handleOpenAlarmTypeDialog = () => {
    setOpenAlarmTypeDialog(true);
  };
  const handleSaveAlarmType = (alarmType) => {};

  const tlmGetAlarmDescription = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/pattern/description/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            language: language,
            type_name: "NOTE",
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    }
  };
  useEffect(() => {
    tlmGetAlarmDescription();
  }, []);

  const tlmModifyDescription = async (alarmTypeId, descriptions) => {
    try {
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/pattern/description/edit",
        options: {
          body: descriptions,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            pattern_type_id: alarmTypeId,
            type_name: "NOTE",
          },
        },
      });
      return restOperation;
    } catch (error) {}
  };

  const tlmDeletePI = async (alarmTypeId) => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/pattern/type/remove",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            pattern_type_id: alarmTypeId, // Passa l'ID dell'allarme come query parameter
          },
        },
      });
      return restOperation;
    } catch (error) {}
  };

  useEffect(() => {
    const tlmGetLanguagesAPI = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/languages",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {},
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        setLanguages(response);
      } catch (error) {
        console.error("Errore durante la chiamata API:", error);
      }
    };
    tlmGetLanguagesAPI();
  }, []);

  const handleSaveLanguage = async (language) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Adding language"),
      });
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/languages/add",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            language: language,
          },
        },
      });

      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Language successfully added"),
        });
        setLanguages((prevLanguages) => [...prevLanguages, language]);
        // Update the languages in DashboardNavbar
        setRefreshLanguages((refreshLanguages) => {
          window.refreshLanguages = refreshLanguages;
        });
        if (window.refreshLanguages) {
          window.refreshLanguages(); // Call the refresh function
        }
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to add language"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to add language"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const languageColumns = useMemo(() => {
    return languages.map((language) => ({
      field: language,
      headerName: I18n.get(formatLanguageName(language)), // Assuming formatLanguageName returns the key for translation
      width: 300,
      editable: true,
    }));
  }, [languages]);

  const columns = useMemo(() => {
    const handleEditClick = (id) => () => {
      setRowModesModel((currentState) => ({
        ...currentState,
        [id]: { mode: GridRowModes.Edit },
      }));
    };

    const handleSaveClick = (id, row) => async () => {
      setRowModesModel((currentState) => ({
        ...currentState,
        [id]: { mode: GridRowModes.View },
      }));
    };

    const handleDeleteClick = (id) => () => {
      setSelectedAlarmTypeIdToDelete(id);
      setDeleteDialogOpen(true);
    };

    const handleCancelClick = (id) => () => {
      setRowModesModel((currentState) => ({
        ...currentState,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
    };

    return [
      ...languageColumns,
      {
        field: "actions",
        type: "actions",
        headerName: I18n.get("Actions"),
        width: 150,
        cellClassName: "actions",
        getActions: ({ id, row }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            return [
              <Tooltip title={I18n.get("Save")}>
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label={I18n.get("Save")}
                  sx={{ color: "primary.main" }}
                  onClick={handleSaveClick(id, row)}
                />
              </Tooltip>,
              <Tooltip title={I18n.get("Cancel")}>
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label={I18n.get("Cancel")}
                  onClick={handleCancelClick(id)}
                />
              </Tooltip>,
            ];
          }
          return [
            <Tooltip title={I18n.get("Edit")}>
              <GridActionsCellItem
                icon={<EditIcon />}
                label={I18n.get("Edit")}
                onClick={handleEditClick(id)}
              />
            </Tooltip>,
            <Tooltip title={I18n.get("Delete")}>
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={I18n.get("Delete")}
                onClick={handleDeleteClick(id)}
                disabled={row.pattern_type_id === 1}
              />
            </Tooltip>,
          ];
        },
      },
    ];
  }, [languageColumns, rowModesModel]);

  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          <Typography variant="h5" mt={0.5} mb={1}>
            {I18n.get("Note Patterns")} (
            {filters ? filteredRowsCount : totalCount})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          justifyContent={tableParentIsMD ? "center" : "space-between"}
          spacing={2}
          sx={{ pb: 2 }}
        >
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Button
              onClick={handleOpenAlarmTypeDialog}
              color="primary"
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth={tableParentIsMD}
            >
              {I18n.get("Add New Note Pattern")}
            </Button>
            <Button
              onClick={handleOpenLanguageDialog}
              color="primary"
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth={tableParentIsMD}
            >
              {I18n.get("Add New Language")}
            </Button>
          </Stack>
          <Input
            type="text"
            placeholder={I18n.get("Search")}
            onChange={searchChanged}
            fullWidth={tableParentIsMD}
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Stack>
      </div>
    );
  };

  const handleRowClick = (params) => {
    const clickedRow = params.row;
    setSelectedRow(clickedRow); // Mantieni la selezione della riga senza aprire lo splitter
  };
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  function getScreenDimension() {
    const screenHeight = window.screen.height;
    if (screenHeight >= 1440) {
      return "27inches";
    } else if (screenHeight >= 1080) {
      return "24inches";
    }
  }
  const screenInches = getScreenDimension();
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const margin =
        browser === "Firefox" ? 200 : browser === "Chrome" ? 200 : 200;
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        margin;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);
  const darkMode = localStorage.getItem("darkMode") === "true";
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
          refreshLanguages={refreshLanguages}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ ml: 3, mt: 3, width: "400px" }}
        >
          <EncodingSettingsMenu />
          <PatternSettingsMenu />
          <EventHistorySettingsMenu />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
              {" "}
              {/** lasciare così */}
              <Card
                sx={{
                  p: 3,
                  maxHeight: `${tableHeight}px`,
                }}
              >
                {renderToolbar()}
                <Box
                  sx={{ p: 2, maxHeight: `${tableHeight}px`, overflow: "auto" }}
                >
                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
                        minHeight: "2rem",
                      },
                      "&  .MuiDataGrid-cell ": {
                        cursor: "default",
                      },
                      borderColor: darkMode ? "#6B635A" : "",
                      "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "--DataGrid-rowBorderColor": darkMode ? "#625a52" : "",
                      minHeight: "20vh",
                      flexFlow: 1,
                      height: "100%",
                    }}
                    localeText={
                      language === "it"
                        ? itIT.components.MuiDataGrid.defaultProps.localeText
                        : language === "ge"
                        ? deDE.components.MuiDataGrid.defaultProps.localeText
                        : ""
                    }
                    rows={filteredRows}
                    columns={columns}
                    // pagination
                    hideFooterRowCount
                    autoPageSize
                    initialState={{
                      density: "compact",
                      pinnedColumns: { right: ["actions"] },
                    }}
                    getRowId={(row) => row.pattern_type_id}
                    editMode="row"
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={(newModel) =>
                      setRowModesModel(newModel)
                    }
                    processRowUpdate={processRowUpdate}
                    onRowEditStop={handleRowEditStop}
                    apiRef={apiRef}
                    onRowClick={handleRowClick}
                    onSelectionModelChange={(newSelection) => {
                      const selectedRows = newSelection.map((id) =>
                        filteredRows.find((row) => row.pattern_type_id === id)
                      );
                    }}
                    resizeThrottleMs={resizeThrottle}
                    loading={!state.fetched || resizeThrottle > 60}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    slotProps={{
                      loadingOverlay: {
                        variant: "skeleton",
                        noRowsVariant: "skeleton",
                      },
                    }}
                    slots={{
                      toolbar: () => (
                        <GridToolbarContainer>
                          <GridToolbarColumnsButton />
                          <GridToolbarExport
                            printOptions={{ disableToolbarButton: true }}
                            csvOptions={{
                              fileName: "Note Pattern",
                              utf8WithBom: true,
                            }}
                          />
                          <GridToolbarFilterButton />
                        </GridToolbarContainer>
                      ),
                    }}
                  />
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </DashboardLayout>
      <AddLanguageDialog
        open={openLanguageDialog}
        onClose={handleCloseLanguageDialog}
        onSave={handleSaveLanguage}
      />
      <AddNotePatterns
        open={openAlarmTypeDialog}
        onClose={() => {
          setOpenAlarmTypeDialog(false);
          tlmGetAlarmDescription();
        }}
        onSave={handleSaveAlarmType}
        languages={languages}
      />
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />

      <ConfirmDeleteDialogNote
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

export default NotePatterns;
